import React, { Fragment, useState, useEffect, useCallback } from 'react';
import TableBuilder, { ApiRequest, ButtonBuilder, ModalButtonBuilder, PairedSelectGroup, RegisteredInputsBuilder, ToastBuilder, ActionGroupBuilder } from '../../../../../../../GlobalComponents/Builders';
import { Row, Col, CardHeader, CardBody, Card, Form, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { H4 } from '../../../../../../../AbstractElements';
import { useForm } from 'react-hook-form';
import { buildSelectOptionsFromArray } from '../../../../../../../GlobalComponents/Helpers';
import ResourceLabel from './ResourceLabel';

const STATUS_COLORS = {
    Approved: '#198754',
    Pending: '#ffc107',
    Missing: '#dc3545',
};

const DefaultResourceMappings = () => {
    const [resourceMappings, setResourceMappings] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);
    const [processingRows, setProcessingRows] = useState({});
    const [services, setServices] = useState([]);
    const [resourceCache, setResourceCache] = useState({});
    const [sourceResources, setSourceResources] = useState([]);
    const [destinationResources, setDestinationResources] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [servicesLocked, setServicesLocked] = useState(false);

    const { register, handleSubmit, setValue, control, reset, watch, formState: { errors } } = useForm();

    const watchSourceService = watch('sourceServiceId');
    const watchDestService = watch('destinationServiceId');

    const [selectedService, setSelectedService] = useState({ left: null, right: null });
    const [selectedResource, setSelectedResource] = useState({ left: null, right: null });
    const [selectedParent, setSelectedParent] = useState({ left: null, right: null });

    useEffect(() => { loadMappings(); loadServices(); }, []);

    const loadMappings = () => {
        ApiRequest('GET', '/dmt/defaults/resourceMappings', { setIsProcessing }).then((results) => {
            if (results.data) setResourceMappings(results.data);
        });
    };

    const loadServices = () => {
        ApiRequest('GET', '/services').then((results) => {
            if (results.data) setServices(results.data);
        });
    };

    const fetchResourcesForServices = useCallback((sourceId, destId) => {
        if (!sourceId || !destId) return;

        const uniqueIds = [...new Set([sourceId, destId].map(Number))];
        const missing = uniqueIds.filter(id => !resourceCache[id]);

        if (missing.length === 0) {
            setSourceResources(resourceCache[Number(sourceId)] || []);
            setDestinationResources(resourceCache[Number(destId)] || []);
            setServicesLocked(true);
            return;
        }

        setLoadingResources(true);
        const promises = missing.map(id =>
            ApiRequest('GET', '/services/{serviceId}/resources', { pathParams: { serviceId: id } }).then(r => ({
                id,
                resources: r.data?.[0]?.resources || []
            }))
        );

        Promise.all(promises).then(results => {
            setResourceCache(prev => {
                const updated = { ...prev };
                results.forEach(r => { updated[r.id] = r.resources; });
                setSourceResources(updated[Number(sourceId)] || []);
                setDestinationResources(updated[Number(destId)] || []);
                return updated;
            });
            setLoadingResources(false);
            setServicesLocked(true);
        });
    }, [resourceCache]);

    useEffect(() => {
        if (watchSourceService && watchDestService) {
            fetchResourcesForServices(watchSourceService, watchDestService);
        }
    }, [watchSourceService, watchDestService]);

    // Auto-fill label when source resource is selected
    useEffect(() => {
        if (selectedResource.left) {
            setValue('label', selectedResource.left.label);
        }
    }, [selectedResource.left]);

    const rowKey = (row) => `${row.source_resource_id}-${row.destination_resource_id}-${row.parent_resource_mapping}`;

    const handleApprove = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/resourceMappings', {
            body: {
                sourceResourceId: row.source_resource_id,
                destinationResourceId: row.destination_resource_id,
                parentResourceMapping: row.parent_resource_mapping,
                isApproved: row.is_approved ? 0 : 1,
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', `Mapping ${row.is_approved ? 'unapproved' : 'approved'}`);
                loadMappings();
            }
        });
    };

    const handleDelete = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/resourceMappings', {
            body: {
                sourceResourceId: row.source_resource_id,
                destinationResourceId: row.destination_resource_id,
                parentResourceMapping: row.parent_resource_mapping,
                action: 'delete',
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', 'Mapping deleted');
                loadMappings();
            }
        });
    };

    const handleCreate = (formData) => {
        if (!selectedResource.left || !selectedResource.right) {
            ToastBuilder('error', 'Please select both resources');
            return;
        }
        if (!selectedParent.left || !selectedParent.right) {
            ToastBuilder('error', 'Please select both parent resources');
            return;
        }
        ApiRequest('POST', '/dmt/defaults/resourceMappings', {
            body: {
                sourceResourceId: selectedResource.left.value,
                destinationResourceId: selectedResource.right.value,
                parentResourceMapping: `${selectedParent.left.value}-${selectedParent.right.value}`,
                label: formData.label || '',
                description: formData.description || '',
                isSetup: parseInt(formData.isSetup),
                isApproved: parseInt(formData.isApproved),
            }
        }).then((results) => {
            if (results?.data?.success) {
                ToastBuilder('success', 'Default mapping created');
                loadMappings();
                resetModal();
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to create mapping');
            }
        });
    };

    const resetModal = () => {
        reset();
        setSourceResources([]);
        setDestinationResources([]);
        setServicesLocked(false);
        setSelectedService({ left: null, right: null });
        setSelectedResource({ left: null, right: null });
        setSelectedParent({ left: null, right: null });
    };

    // Build service name lookup from loaded services
    const serviceNameMap = services.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});

    // Build tabs from mapping data
    const getServiceComboKey = (row) => `${row.source_service_code || '?'} → ${row.destination_service_code || '?'}`;
    const getServiceComboLabel = (row) => {
        const srcService = services.find(s => s.code === row.source_service_code);
        const dstService = services.find(s => s.code === row.destination_service_code);
        const srcName = serviceNameMap[srcService?.id] || row.source_service_code || '?';
        const dstName = serviceNameMap[dstService?.id] || row.destination_service_code || '?';
        if (row.source_service_code === row.destination_service_code) return srcName;
        return `${srcName} → ${dstName}`;
    };

    const tabCombos = [...new Set(resourceMappings.map(getServiceComboKey))];
    const tabLabels = {};
    resourceMappings.forEach(row => { tabLabels[getServiceComboKey(row)] = getServiceComboLabel(row); });

    const serviceOptions = buildSelectOptionsFromArray(services, { label: 'name', value: 'id' });
    const sourceResourceOptions = buildSelectOptionsFromArray(sourceResources, { label: 'name', value: 'resource_id' });
    const destResourceOptions = buildSelectOptionsFromArray(destinationResources, { label: 'name', value: 'resource_id' });
    const parentSourceOptions = buildSelectOptionsFromArray(sourceResources.filter(r => !selectedResource.left || r.resource_id !== selectedResource.left.value), { label: 'name', value: 'resource_id' });
    const parentDestOptions = buildSelectOptionsFromArray(destinationResources.filter(r => !selectedResource.right || r.resource_id !== selectedResource.right.value), { label: 'name', value: 'resource_id' });

    const filteredMappings = activeTab === 'all' ? resourceMappings : resourceMappings.filter(r => getServiceComboKey(r) === activeTab);

    const resourcesReady = !loadingResources && sourceResources.length > 0 && destinationResources.length > 0;

    const pairedRows = [
        {
            label: 'Service',
            leftOptions: serviceOptions,
            rightOptions: serviceOptions,
            leftValue: selectedService.left,
            rightValue: selectedService.right,
            onLeftChange: (opt) => { setSelectedService(prev => ({ ...prev, left: opt })); setValue('sourceServiceId', opt?.value || ''); },
            onRightChange: (opt) => { setSelectedService(prev => ({ ...prev, right: opt })); setValue('destinationServiceId', opt?.value || ''); },
            isDisabled: servicesLocked,
        },
        ...(resourcesReady ? [
            {
                label: 'Resource',
                leftOptions: sourceResourceOptions,
                rightOptions: destResourceOptions,
                leftValue: selectedResource.left,
                rightValue: selectedResource.right,
                onLeftChange: (opt) => setSelectedResource(prev => ({ ...prev, left: opt })),
                onRightChange: (opt) => setSelectedResource(prev => ({ ...prev, right: opt })),
            },
            {
                label: 'Parent',
                leftOptions: parentSourceOptions,
                rightOptions: parentDestOptions,
                leftValue: selectedParent.left,
                rightValue: selectedParent.right,
                onLeftChange: (opt) => setSelectedParent(prev => ({ ...prev, left: opt })),
                onRightChange: (opt) => setSelectedParent(prev => ({ ...prev, right: opt })),
            },
        ] : []),
    ];

    return (
        <Row>
            <Col lg={12}>
                <Card>
                    <CardHeader className='d-flex justify-content-between align-items-center' style={{ paddingBottom: 0 }}>
                        <div style={{ maxWidth: '80%' }}>
                            <H4 attrH4={{ className: 'card-title mb-0' }}>Default Resource Mappings</H4>
                            <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: 2, marginTop: 4 }}>Which standard resources pair together across services, and the dependency chains they form.</p>
                            <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
                                Pre-configured resource pairings for service-to-service migrations. These populate the "Select Mappings" menu when setting up a new job.
                                Approved mappings appear as selectable options with their dependency chains. Table: <code>dmt_default_resource_mapping</code>
                            </p>
                        </div>
                        <ModalButtonBuilder
                            title='Add Mapping'
                            modalTitle='Add Default Resource Mapping'
                            className='btn btn-hover-primary'
                            formId='add-default-mapping-form'
                            style={{ minWidth: 650 }}
                            onClick={resetModal}
                            body={
                                <Form id='add-default-mapping-form' onSubmit={handleSubmit(handleCreate)}>
                                    <RegisteredInputsBuilder
                                        control={control}
                                        registrator={register}
                                        registratorErrors={errors}
                                        inputs={[
                                            { type: 'custom', size: 12, render:
                                                <PairedSelectGroup
                                                    leftHeader='Source'
                                                    rightHeader='Destination'
                                                    rows={pairedRows}
                                                    message={loadingResources ? 'Loading resources...' : null}
                                                />
                                            },
                                            ...(resourcesReady ? [
                                                {
                                                    label: 'Label',
                                                    name: 'label',
                                                    type: 'text',
                                                    size: 12,
                                                },
                                                {
                                                    label: 'Description',
                                                    name: 'description',
                                                    type: 'text',
                                                    size: 12,
                                                },
                                                {
                                                    label: 'Is Setup',
                                                    name: 'isSetup',
                                                    type: 'reactselect',
                                                    size: 12,
                                                    options: buildSelectOptionsFromArray([{ id: 0, value: 'No' }, { id: 1, value: 'Yes' }], { label: 'value', value: 'id' }),
                                                    value: 0,
                                                },
                                                {
                                                    label: 'Is Approved',
                                                    name: 'isApproved',
                                                    type: 'reactselect',
                                                    size: 12,
                                                    options: buildSelectOptionsFromArray([{ id: 0, value: 'No' }, { id: 1, value: 'Yes' }], { label: 'value', value: 'id' }),
                                                },
                                            ] : []),
                                        ]}
                                    />
                                </Form>
                            }
                        />
                    </CardHeader>
                    <CardBody>
                        <Nav tabs>
                            <NavItem>
                                <NavLink href="#" className={activeTab === 'all' ? 'active' : ''} onClick={() => setActiveTab('all')}>All</NavLink>
                            </NavItem>
                            {tabCombos.map(combo =>
                                <NavItem key={combo}>
                                    <NavLink href="#" className={activeTab === combo ? 'active' : ''} onClick={() => setActiveTab(combo)}>{tabLabels[combo]}</NavLink>
                                </NavItem>
                            )}
                        </Nav>
                        <TabContent activeTab={activeTab}>
                            <TabPane className="fade show" tabId={activeTab}>
                                <TableBuilder
                                    subHeader={true}
                                    columnsToSearch={['label', 'description', 'source_resource', 'destination_resource', 'source_service_code', 'destination_service_code', 'parent_resource_mapping']}
                                    data={filteredMappings}
                                    processing={isProcessing}
                                    pagination={true}
                                    columns={[
                                        {
                                            name: 'Status',
                                            selector: (row) => <span className='badge' style={{ backgroundColor: STATUS_COLORS[row.is_approved ? 'Approved' : 'Pending'] }}>
                                                {row.is_approved ? 'Approved' : 'Pending'}
                                            </span>,
                                            sortable: true,
                                            center: true,
                                            maxWidth: '9%',
                                            sortFunction: (a, b) => a.is_approved - b.is_approved,
                                        },
                                        {
                                            name: 'Belongs To',
                                            selector: (row) => {
                                                const src = row.parent_source_resource;
                                                const dst = row.parent_destination_resource;
                                                if (!src && !dst) return row.parent_resource_mapping;
                                                const label = `${src || '?'} → ${dst || '?'}`;
                                                return <div style={{ textAlign: 'center' }}>
                                                    <div>{label}</div>
                                                    <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{row.parent_resource_mapping}</div>
                                                </div>;
                                            },
                                            sortable: true,
                                            center: true,
                                            maxWidth: '18%',
                                        },
                                        {
                                            name: 'Mapping',
                                            selector: (row) => <div style={{ display: 'inline-grid', gridTemplateColumns: '1fr auto 1fr', gap: '0 6px', alignItems: 'baseline' }}>
                                                <span style={{ textAlign: 'right' }}>{row.source_resource}</span>
                                                <span style={{ fontWeight: 500 }}>→</span>
                                                <span style={{ textAlign: 'left' }}>{row.destination_resource}</span>
                                                <span style={{ textAlign: 'right', fontSize: '10px', lineHeight: 1.2, color: '#9ca3af' }}>{row.source_service_code}</span>
                                                <span style={{ fontSize: '10px', lineHeight: 1.2, color: '#9ca3af', fontWeight: 500 }}>→</span>
                                                <span style={{ textAlign: 'left', fontSize: '10px', lineHeight: 1.2, color: '#9ca3af' }}>{row.destination_service_code}</span>
                                            </div>,
                                            sortable: true,
                                            center: true,
                                            sortFunction: (a, b) => (a.source_resource || '').localeCompare(b.source_resource || ''),
                                        },
                                        {
                                            name: 'Label',
                                            selector: (row) => row.label || '-',
                                            sortable: true,
                                            maxWidth: '25%',
                                        },
                                        {
                                            name: 'Type',
                                            selector: (row) => row.is_setup ? 'Setup' : 'Data',
                                            sortable: true,
                                            center: true,
                                            maxWidth: '8%',
                                        },
                                        {
                                            name: 'Actions',
                                            center: true,
                                            maxWidth: '11%',
                                            selector: (row) => (
                                                <ActionGroupBuilder
                                                    isApproved={row.is_approved}
                                                    disabled={processingRows[rowKey(row)]}
                                                    onApprove={() => handleApprove(row)}
                                                    onDelete={() => handleDelete(row)}
                                                    modalTitle='Default Resource Mapping Details'
                                                    modalBody={
                                                        <table className='table table-borderless table-sm mb-0'>
                                                            <tbody>
                                                                <tr><td className='text-muted' style={{width:'40%'}}>Source</td><td>{row.source_resource} ({row.source_service_code})</td></tr>
                                                                <tr><td className='text-muted'>Destination</td><td>{row.destination_resource} ({row.destination_service_code})</td></tr>
                                                                <tr><td className='text-muted'>Parent Mapping</td><td>{row.parent_source_resource && row.parent_destination_resource ? `${row.parent_source_resource} → ${row.parent_destination_resource}` : row.parent_resource_mapping}</td></tr>
                                                                <tr><td className='text-muted'>Label</td><td>{row.label || '-'}</td></tr>
                                                                <tr><td className='text-muted'>Description</td><td>{row.description || '-'}</td></tr>
                                                                <tr><td className='text-muted'>Setup</td><td>{row.is_setup ? 'Yes' : 'No'}</td></tr>
                                                                <tr><td className='text-muted'>Approved</td><td>{row.is_approved ? 'Yes' : 'No'}</td></tr>
                                                            </tbody>
                                                        </table>
                                                    }
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </TabPane>
                        </TabContent>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default DefaultResourceMappings;